import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n/config';

const LanguageCurrencyContext = createContext();

export const useLanguageCurrency = () => {
  const context = useContext(LanguageCurrencyContext);
  if (!context) {
    throw new Error('useLanguageCurrency must be used within a LanguageCurrencyProvider');
  }
  return context;
};

// Helper function to format phone numbers with Arabic-Indic numerals for Arabic language
export const formatPhoneNumber = (phoneNumber, language) => {
  if (language === 'ar') {
    // Arabic-Indic numerals mapping: 0-9 to ٠-٩
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return phoneNumber.replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)]);
  }
  return phoneNumber;
};

// Currency exchange rates (base: EGP)
// These can be updated from an API or central bank rates
const DEFAULT_EXCHANGE_RATES = {
  EGP: 1,
  USD: 0.0203, // Approximate rate (1 EGP = 0.0203 USD)
  EUR: 0.0187,
  GBP: 0.0160,
  SAR: 0.0761,
  AED: 0.0745,
  KWD: 0.0062,
  QAR: 0.0740,
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  EGP: 'EGP',
  USD: '$',
  EUR: '€',
  GBP: '£',
  SAR: 'SAR',
  AED: 'AED',
  KWD: 'KWD',
  QAR: 'QAR',
};

// Country to currency mapping
const COUNTRY_TO_CURRENCY = {
  EG: 'EGP',
  US: 'USD',
  GB: 'GBP',
  SA: 'SAR',
  AE: 'AED',
  KW: 'KWD',
  QA: 'QAR',
  // Add more countries as needed
};

export const LanguageCurrencyProvider = ({ children }) => {
  // Get saved preferences from localStorage or use defaults
  const [language, setLanguage] = useState(() => {
    return i18n.language || 'ar'; // Default to Arabic
  });

  const [currency, setCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem('currency');
      if (saved) return saved;
    } catch (e) {}
    
    // Try to detect currency based on user's country
    // This is a simple implementation - in production, you'd use a geolocation API
    const detectedCurrency = detectCurrencyFromCountry();
    return detectedCurrency || 'EGP';
  });

  const [exchangeRates, setExchangeRates] = useState(DEFAULT_EXCHANGE_RATES);
  const [isRTL, setIsRTL] = useState(language === 'ar');

  // Detect currency based on user's country
  function detectCurrencyFromCountry() {
    // Try to get country from browser timezone or locale
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Simple timezone to country mapping
      if (timezone.includes('Cairo') || timezone.includes('Africa/Cairo')) {
        return 'EGP';
      } else if (timezone.includes('America')) {
        return 'USD';
      } else if (timezone.includes('Europe/London')) {
        return 'GBP';
      } else if (timezone.includes('Riyadh') || timezone.includes('Asia/Riyadh')) {
        return 'SAR';
      } else if (timezone.includes('Dubai') || timezone.includes('Asia/Dubai')) {
        return 'AED';
      }
    } catch (e) {
      console.error('Error detecting currency:', e);
    }
    return null;
  }

  // Fetch exchange rates from API (optional - can use a free API like exchangerate-api.com)
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        // Using exchangerate-api.com free tier (no API key needed for base currency EGP)
        // Note: This API might not support EGP as base, so we'll use a fallback
        // For production, use a paid API or central bank API
        
        // Alternative: Use a free API that supports EGP
        // For now, we'll use the default rates and allow manual updates
        // You can implement API calls here if needed
        
        // Example API call (commented out - implement when you have API access):
        /*
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EGP');
        const data = await response.json();
        if (data.rates) {
          setExchangeRates({
            EGP: 1,
            USD: data.rates.USD || DEFAULT_EXCHANGE_RATES.USD,
            EUR: data.rates.EUR || DEFAULT_EXCHANGE_RATES.EUR,
            GBP: data.rates.GBP || DEFAULT_EXCHANGE_RATES.GBP,
            SAR: data.rates.SAR || DEFAULT_EXCHANGE_RATES.SAR,
            AED: data.rates.AED || DEFAULT_EXCHANGE_RATES.AED,
            KWD: data.rates.KWD || DEFAULT_EXCHANGE_RATES.KWD,
            QAR: data.rates.QAR || DEFAULT_EXCHANGE_RATES.QAR,
          });
        }
        */
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
        // Use default rates on error
      }
    };

    // Fetch rates on mount and every hour
    fetchExchangeRates();
    const interval = setInterval(fetchExchangeRates, 3600000); // Update every hour

    return () => clearInterval(interval);
  }, []);

  // Update language in i18n and RTL when language changes
  useEffect(() => {
    i18n.changeLanguage(language);
    setIsRTL(language === 'ar');
    // Update document direction
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    try {
      localStorage.setItem('currency', currency);
    } catch (e) {}
  }, [currency]);

  // Convert price from EGP to selected currency
  const convertPrice = (priceInEGP) => {
    if (!priceInEGP || isNaN(priceInEGP)) return 0;
    const rate = exchangeRates[currency] || 1;
    return priceInEGP * rate;
  };

  // Format price with currency symbol
  const formatPrice = (priceInEGP, showDecimals = true) => {
    const convertedPrice = convertPrice(priceInEGP);
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    
    if (showDecimals) {
      return `${symbol} ${convertedPrice.toFixed(2)}`;
    } else {
      return `${symbol} ${Math.round(convertedPrice)}`;
    }
  };

  // Update exchange rates manually (for admin or when API updates)
  const updateExchangeRates = (newRates) => {
    setExchangeRates({ ...DEFAULT_EXCHANGE_RATES, ...newRates });
  };

  const value = {
    language,
    setLanguage,
    currency,
    setCurrency,
    isRTL,
    convertPrice,
    formatPrice,
    exchangeRates,
    updateExchangeRates,
    currencySymbol: CURRENCY_SYMBOLS[currency] || currency,
  };

  return (
    <LanguageCurrencyContext.Provider value={value}>
      {children}
    </LanguageCurrencyContext.Provider>
  );
};
